import { ChevronDown } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { Button as AriaButton } from 'react-aria-components';

import { BadgeWithIcon } from '@/lib/base/badges/badges';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { cx } from '@/utils/cx';

import type {
  TriggerButtonTarget,
  TriggerContextItem,
} from './ActionOverview.helpers';

/**
 * The compact presentation of the proposal header's trigger context: a
 * single "Context N ⌄" pill that opens a dropdown listing every context
 * item. The pill takes the sanctioned soft brand tint while any of its
 * records is open on the slideout stack; the open record's menu row carries
 * the dropdown's active treatment (bg-active + aria-current + trailing brand
 * check). Picking a row activates its target exactly like clicking the
 * expanded chip would.
 *
 * Presentational: items arrive fully resolved from the orchestrator
 * (TriggerShortcutButtons). Pill-as-dropdown-trigger structure follows
 * EmailThreadVisibilityBadge (the chevron renders INSIDE the capsule via
 * iconTrailing — a chevron floating beside the pill reads as a detached
 * fragment), with the canonical focus recipe rather than that component's
 * legacy ring.
 */
interface TriggerContextPillProps {
  /** The resolved context items. Non-empty. */
  items: TriggerContextItem[];
  onActivate: (target: TriggerButtonTarget) => void;
  /** Visible pill label before the count. Default 'Context'. */
  label?: string;
}

const PILL_TRIGGER_CLASS =
  'inline-flex w-fit shrink-0 cursor-pointer items-center rounded-full outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2';

const PILL_HOVER_GRAY =
  'transition duration-100 ease-linear hover:bg-utility-gray-100';
const PILL_HOVER_BRAND =
  'transition duration-100 ease-linear hover:bg-utility-brand-100';

export function TriggerContextPill({
  items,
  onActivate,
  label = 'Context',
}: TriggerContextPillProps) {
  const openCount = useMemo(
    () => items.filter(item => item.isOpen).length,
    [items]
  );
  const anyOpen = openCount > 0;

  const handleAction = useCallback(
    (key: Key) => {
      const item = items.find(i => i.key === key);
      if (item) {
        onActivate(item.target);
      }
    },
    [items, onActivate]
  );

  const visibleLabel = `${label} ${items.length}`;

  return (
    <Dropdown.Root instance='trigger-context'>
      <AriaButton
        className={PILL_TRIGGER_CLASS}
        // Accessible name comes from content ("Context 3") when idle; with a
        // record open, a superset name also conveys the open state (WCAG
        // 2.5.3 — the visible text stays a prefix of the accessible name).
        aria-label={anyOpen ? `${visibleLabel}, ${openCount} open` : undefined}
      >
        <BadgeWithIcon
          type='pill-color'
          size='sm'
          color={anyOpen ? 'brand' : 'gray'}
          iconTrailing={ChevronDown}
          className={cx(anyOpen ? PILL_HOVER_BRAND : PILL_HOVER_GRAY)}
        >
          {visibleLabel}
        </BadgeWithIcon>
      </AriaButton>
      <Dropdown.Popover placement='bottom right' className='min-w-[200px]'>
        <Dropdown.Menu selectionMode='none' onAction={handleAction}>
          {items.map(item => (
            <Dropdown.Item
              key={item.key}
              id={item.key}
              label={item.label}
              // Bare noun stays the visible label and typeahead value; the
              // accessible name keeps the action verb ("View Ada Lovelace"),
              // matching TriggerContextChip (WCAG 2.5.3 label-in-name superset).
              textValue={item.label}
              aria-label={`View ${item.label}`}
              icon={item.icon}
              isActive={item.isOpen}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
